/**
 * chain-eos/mechat/paycashFiles.ts — client for PayCash's file store, so
 * ProperWallet can SEND file attachments the PayCash app can open (the receive
 * side lives in `crypto.ts#decryptPayCashFile` + the app's PayCashFileAttachment).
 *
 * Reverse-engineered from the official PayCash APK (2026-09-06):
 *  - `FiatManager.getAuthHeader` / `AuthApi` — mint a short-lived JWT.
 *  - `ChatManager$uploadFile$2` / `FilesApi` — multipart upload of the (already
 *    AES-encrypted) bytes, authorised with that JWT.
 *
 * ── Auth (mint a JWT) ────────────────────────────────────────────────────────
 * POST <authUrl>  (app PROD: https://auth.paycash.online/v1/auth/tokens)
 *   headers: Content-Type: application/json,  X-Signature: SIG_K1_…
 *   body:    {"auds":["<aud>"],"eos_account":"<account>","exp":"<exp>"}
 * The X-Signature is a standard EOS/Antelope signHash: a canonical secp256k1
 * signature over sha256(<body bytes>), i.e. PrivateKey.signDigest(sha256(body)).
 * `exp` is ~30s in the future, formatted `yyyy-MM-dd'T'HH:mm:ssZ` (local offset).
 * The reply is {"tokens":[{"site_name","jwt"}]}; the upload Authorization header
 * is the literal string "JWT " + tokens[0].jwt.
 *
 * ── Upload ───────────────────────────────────────────────────────────────────
 * POST <uploadUrl>  (app PROD: https://files.paycash.online/api/v1/files/upload)
 *   headers: authorization: JWT <jwt>
 *   multipart: file=<encrypted bytes>, retention="0"
 * Reply body is a JSON string {"url","expires_at","size","sha256"}; `url` is what
 * goes inside the `<file>url;name;size;ext</file>` chat message.
 *
 * ── Why the proxy defaults ───────────────────────────────────────────────────
 * files.paycash.online / auth.paycash.online send no CORS headers AND gate on a
 * PayCash-specific User-Agent (a browser can't set User-Agent, and can't read a
 * no-CORS response). So by default we go through the app's own same-origin
 * proxies — `/paycash-auth-proxy` and `/paycash-upload-proxy` — which inject the
 * accepted User-Agent server-side and re-serve same-origin, exactly like the
 * `/paycash-file-proxy` download proxy. The signature is always computed here,
 * client-side; the private key never reaches the proxy.
 */

import { Bytes, Checksum256, PrivateKey } from '@wharfkit/antelope'
import { proxyUrl } from '../proxy-base.js'

/** Default audience the PayCash app requests a token for (its swap backend). */
export const PAYCASH_DEFAULT_AUD = 'https://api.paycashswap.com'
/** Retention value the app sends verbatim (`Secp256k.A` == "0"). */
export const PAYCASH_DEFAULT_RETENTION = '0'
/** Same-origin proxy the app serves (see api/paycash-auth-proxy + vite.config). */
export const PAYCASH_AUTH_PROXY = '/paycash-auth-proxy'
/** Same-origin proxy the app serves (see api/paycash-upload-proxy + vite.config). */
export const PAYCASH_UPLOAD_PROXY = '/paycash-upload-proxy'

export interface PayCashUploadConfig {
  /** Where to POST the token request. Default: the same-origin auth proxy. */
  authUrl?: string
  /** Where to POST the multipart upload. Default: the same-origin upload proxy. */
  uploadUrl?: string
  /** Token audience. Default: PAYCASH_DEFAULT_AUD. */
  aud?: string
  /** Upload retention flag. Default: PAYCASH_DEFAULT_RETENTION. */
  retention?: string
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Formats a Date as `yyyy-MM-dd'T'HH:mm:ssZ` in local time with a numeric offset
 * (e.g. `2026-09-06T20:00:30+0300`) — matching the app's Java
 * `SimpleDateFormat("yyyy-MM-dd'T'HH:mm:ssZ")` with the device timezone.
 */
export function formatFiatExp(date: Date): string {
  const offMin = -date.getTimezoneOffset() // minutes east of UTC
  const sign = offMin >= 0 ? '+' : '-'
  const abs = Math.abs(offMin)
  const y = date.getFullYear()
  const m = pad2(date.getMonth() + 1)
  const d = pad2(date.getDate())
  const hh = pad2(date.getHours())
  const mm = pad2(date.getMinutes())
  const ss = pad2(date.getSeconds())
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}${sign}${pad2(Math.floor(abs / 60))}${pad2(abs % 60)}`
}

/**
 * Mints a PayCash upload JWT for `account`, signing the request with its key.
 * Returns the ready `Authorization` header value (`"JWT <jwt>"`).
 *
 * The private key is used only to sign the request digest locally; it is never
 * sent anywhere.
 */
export async function requestPayCashUploadToken(params: {
  account: string
  privateKey: string | PrivateKey
  config?: PayCashUploadConfig
  /** Injectable clock/fetch for tests. */
  now?: () => number
  fetchImpl?: typeof fetch
}): Promise<string> {
  const cfg = params.config ?? {}
  const authUrl = cfg.authUrl ?? proxyUrl(PAYCASH_AUTH_PROXY)
  const aud = cfg.aud ?? PAYCASH_DEFAULT_AUD
  const doFetch = params.fetchImpl ?? fetch
  const nowMs = (params.now ?? Date.now)()

  const exp = formatFiatExp(new Date(nowMs + 30_000))
  // Field order matters only in that we sign exactly the bytes we send.
  const body = JSON.stringify({ auds: [aud], eos_account: params.account, exp })
  const digest = Checksum256.hash(Bytes.from(body, 'utf8'))
  const signature = PrivateKey.from(params.privateKey).signDigest(digest).toString()

  const res = await doFetch(authUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Signature': signature },
    body,
  })
  if (!res.ok) {
    throw new Error(`[mechat] PayCash auth failed (${res.status})`)
  }
  const json = (await res.json()) as { tokens?: Array<{ jwt?: string }> }
  const jwt = json?.tokens?.[0]?.jwt
  if (!jwt) throw new Error('[mechat] PayCash auth returned no token')
  return `JWT ${jwt}`
}

/**
 * Uploads already-encrypted file bytes to PayCash's file store and returns the
 * URL to embed in the `<file>…</file>` chat message.
 */
export async function uploadPayCashFile(params: {
  authorization: string
  encryptedFile: Uint8Array
  fileName: string
  contentType: string
  config?: PayCashUploadConfig
  fetchImpl?: typeof fetch
}): Promise<string> {
  const cfg = params.config ?? {}
  const uploadUrl = cfg.uploadUrl ?? proxyUrl(PAYCASH_UPLOAD_PROXY)
  const retention = cfg.retention ?? PAYCASH_DEFAULT_RETENTION
  const doFetch = params.fetchImpl ?? fetch

  const form = new FormData()
  // Copy into a standalone ArrayBuffer so Blob's typings accept it under strict tsc.
  const buf = new ArrayBuffer(params.encryptedFile.byteLength)
  new Uint8Array(buf).set(params.encryptedFile)
  form.append('file', new Blob([buf], { type: params.contentType }), params.fileName)
  form.append('retention', retention)

  // Do NOT set Content-Type — the runtime adds the multipart boundary itself.
  const res = await doFetch(uploadUrl, {
    method: 'POST',
    headers: { authorization: params.authorization },
    body: form,
  })
  if (!res.ok) {
    throw new Error(`[mechat] PayCash upload failed (${res.status})`)
  }
  // The endpoint replies with a JSON string body {"url",…}. fetch's .json()
  // parses it; guard against a double-encoded string just in case.
  let parsed: unknown = await res.json()
  if (typeof parsed === 'string') parsed = JSON.parse(parsed)
  const url = (parsed as { url?: string })?.url
  if (!url) throw new Error('[mechat] PayCash upload returned no url')
  return url
}

/** Builds the `<file>url;name;size;ext</file>` tag PayCash's chat client parses. */
export function buildFileTag(params: {
  url: string
  fileName: string
  fileSize: number
  fileExt: string
}): string {
  return `<file>${params.url};${params.fileName};${params.fileSize};${params.fileExt}</file>`
}
